import { Box, Card, CardContent, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { isBhiAdmin } from 'src/helpers/permissions';
import { AdminCompany } from 'src/modules/companies/types';
import { ProgressBar } from '../ProgressBar';
import { creditDividerSx } from '../../helpers';
import OnboardingWidget from '../OnboardingWidget';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProfileLike = any;

export default function SubscriptionCard({
  company,
  companyLoading,
  isMobile,
  creditsInPercents,
  selectedCompanyId,
  profile,
  onCompanyRefetch,
}: {
  company?: AdminCompany;
  companyLoading: boolean;
  isMobile: boolean;
  creditsInPercents: number;
  selectedCompanyId?: string;
  profile: ProfileLike;
  onCompanyRefetch: () => void;
}) {
  const figureFontSize = isMobile ? '0.875rem' : 'inherit';
  const captionFontSize = isMobile ? '0.75rem' : 'inherit';
  const noticeBgcolor = company?.sandBoxMode ? 'warning.lighter' : 'transparent';

  return (
    <Card variant="outlined" sx={{ mt: 2.5, width: isMobile ? '100%' : '75%', minHeight: 176 }}>
      <CardContent sx={{ minHeight: 176, p: 0, pb: '0px !important' }}>
        {companyLoading && (
          <Box width="100%" height="100%">
            <CircularProgress />
          </Box>
        )}
        {!companyLoading && company?.onboardingStatus === 'completed' && (
          <>
            {company?.subscription && (
              <Box px={2} pt={1.25}>
                <Box display="inline-block" pt={1.625} fontWeight="fontWeightBold" fontSize={figureFontSize}>
                  {company?.subscription?.plan?.name}:
                </Box>{' '}
                <Box component="span" fontSize={figureFontSize}>
                  {company?.subscription?.startType === 'onboarding_complete'
                    ? 'Complete Account Setup to activate.'
                    : dayjs(company?.subscription?.startDate).format('MM/DD/YYYY') +
                      ' - ' +
                      dayjs(company?.subscription?.endDate).format('MM/DD/YYYY')}
                </Box>
                <Box my={2.5}>
                  <ProgressBar thickness={20} variant="determinate" value={creditsInPercents} />
                </Box>
                <Stack direction="row">
                  <Box width="33%">
                    <Box mb={1.25} fontWeight="fontWeightBold" fontSize={figureFontSize}>
                      {company.subscription?.creditsRemaining
                        ? Math.max(company.subscription.creditsRemaining || 0, 0).toLocaleString()
                        : 0}
                    </Box>
                    <Box fontSize={captionFontSize}>Credits Available In Plan</Box>
                  </Box>
                  <Divider sx={creditDividerSx} orientation="vertical" flexItem />
                  <Box width="33%">
                    <Box mb={1.25} fontWeight="fontWeightBold">
                      {company.subscription?.creditsUsed?.toLocaleString() || 0}
                    </Box>
                    <Box fontSize={captionFontSize}>Credits Used</Box>
                  </Box>
                  <Divider sx={creditDividerSx} orientation="vertical" flexItem />
                  <Box width="33%">
                    <Box mb={1.25} fontWeight="fontWeightBold" fontSize={figureFontSize}>
                      {company?.subscription?.plan?.credits?.toLocaleString() || 0}
                    </Box>
                    <Box fontSize={captionFontSize}>Credits In Plan</Box>
                  </Box>
                </Stack>
              </Box>
            )}
            {!company?.subscription && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="100%"
                minHeight={176}
                mt={0}
                bgcolor={noticeBgcolor}
                fontWeight="fontWeightBold"
                fontSize={figureFontSize}
              >
                There are no active subscriptions
              </Box>
            )}
          </>
        )}

        {!companyLoading && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="100%"
            height="100%"
            mt={0}
            bgcolor={noticeBgcolor}
            fontWeight="fontWeightBold"
            fontSize={figureFontSize}
          >
            {company?.sandBoxMode ? (
              <Stack direction="column" alignItems="center" spacing={0.5}>
                <Typography
                  variant={isMobile ? 'body1' : 'h6'}
                  fontWeight="fontWeightBold"
                  fontSize={figureFontSize}
                >
                  Playground Mode
                </Typography>
                <Typography
                  variant={isMobile ? 'caption' : 'body2'}
                  fontWeight="fontWeightRegular"
                  textAlign="center"
                >
                  Messages sent via campaigns and inbox are in test mode and won’t be sent to contacts.{' '}
                  <br />
                </Typography>
              </Stack>
            ) : (
              company &&
              company.onboardingStatus &&
              company.onboardingStatus !== 'completed' &&
              (isBhiAdmin(profile) || profile?.role?.name === 'company_admin') && (
                <OnboardingWidget
                  selectedCompanyId={selectedCompanyId}
                  company={company}
                  profile={profile}
                  onCompanyRefetch={onCompanyRefetch}
                />
              )
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
